import { create, useModal } from '@ebay/nice-modal-react';
import Popup from '@shared/components/Popup/Popup';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Devices } from 'assets/icons/Devices.svg';
import { ReactComponent as DeviceUnverified } from 'assets/icons/DeviceUnverified.svg';
import { ReactComponent as LockPin } from 'assets/icons/LockPin.svg';
import { ReactComponent as Logout } from 'assets/icons/Logout.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import SelectionButtonWithIcon from 'components/SelectionButtonWithIcon';
import {
  clearLoginSasVerificationPending,
  completeLoginSecretStorageUnlock,
  getLoginSecretStorageUnlockPhase,
  invokeLoginSecretStorageUnlockCancel,
  isLoginSecretStorageUnlockActive,
  isLoginUnlockBrokenIdentity,
  isLoginUnlockResetCompleted,
  markLoginSasVerificationPending,
  submitLoginSecretStorageUnlock,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useHasSecurityCode from 'contexts/MatrixDeviceContext/useHasSecurityCode';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import { SessionStateKeys, useSessionState } from 'hooks/useSessionState';
import useLogout from 'modules/Authentication/hooks/useLogout';
import ResetKeysModal from 'modules/EndToEndEncryption/ResetKeysModal/ResetKeysModal';
import UnlockWithSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/UnlockWithSecurityCodeModal/UnlockWithSecurityCodeModal';
import testingConstants from 'utils/testingConstants';

interface ChooseVerificationMethodModalProps {
  onVerificationComplete?: () => void;
  /** When true, shown during login unlock — closing cancels SSO and returns to /auth. */
  isLoginUnlock?: boolean;
  onLoginUnlockCancel?: () => void;
}

const ChooseVerificationMethodModal = create<ChooseVerificationMethodModalProps>(
  ({ onVerificationComplete, isLoginUnlock: isLoginUnlockProp, onLoginUnlockCancel }) => {
    const { t } = useTranslation();
    const modal = useModal(ChooseVerificationMethodModal);
    const unlockWithSecurityCodeModal = useModal(UnlockWithSecurityCodeModal);
    const resetKeysModal = useModal(ResetKeysModal);
    const { matrixClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
    const logout = useLogout();
    const { setHasChosenSasVerification, beginWaitingDeviceFlow, cancelVerification } =
      useMatrixDeviceVerificationContext();
    const { hasSecurityCode, isLoading: isPinLoading } = useHasSecurityCode();
    const {
      devicesVerificationStatus,
      crossSigningStatus,
      checkForVerifiedDevices,
      isLoadingVerifiedDevices,
      isResettingKeys,
      hasCompletedSelfVerification,
      refreshHasSecurityCode,
      isAccountRemotelyInvalidated,
    } = useMatrixDeviceContext();
    const [needsSecurityCodePromptAfterBootstrap] = useSessionState<boolean>(
      SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP,
      false
    );

    const isLoginUnlock = isLoginUnlockProp ?? isLoginSecretStorageUnlockActive();
    const loginUnlockPhase = getLoginSecretStorageUnlockPhase();

    useEffect(() => {
      checkForVerifiedDevices();
      if (isLoginUnlock) refreshHasSecurityCode();
    }, [checkForVerifiedDevices, isLoginUnlock, refreshHasSecurityCode]);

    useEffect(() => {
      if (!isLoginUnlock || !hasCompletedSelfVerification) return;

      clearLoginSasVerificationPending();
      modal.remove();
      completeLoginSecretStorageUnlock();
      onVerificationComplete?.();
    }, [hasCompletedSelfVerification, isLoginUnlock, modal, onVerificationComplete]);

    // `hasSecurityCode === false` (strict): a `null` value means the SSSS probe is still loading
    // (e.g. the post-create-code `refreshHasSecurityCode` window). Treating `null` as "no security
    // code" via `!hasSecurityCode` let the reset section auto-open during that window (BUG-027).
    const hasNoVerificationOptions =
      !isLoginUnlock &&
      !isPinLoading &&
      hasSecurityCode === false &&
      devicesVerificationStatus === CrossSigningStatusEnum.NOT_VERIFIED;

    // A reset that just succeeded leaves this single device with no security code yet and
    // `devicesVerificationStatus === NOT_VERIFIED` (the device-list check skips the current device),
    // which would otherwise re-trigger `showResetSection` and re-open ResetKeysModal on top of the
    // create-security-code prompt. Suppress the reset section once the current device is verified, a
    // create-code prompt is already scheduled, or a reset completed this login session (BUG-018).
    // Also suppress when another device reset keys — the idle device should see the picker, not be
    // forced straight into ResetKeysModal while the device list is still settling (BUG-023).
    const suppressAutoReset =
      crossSigningStatus === CrossSigningStatusEnum.VERIFIED ||
      needsSecurityCodePromptAfterBootstrap === true ||
      isLoginUnlockResetCompleted() ||
      isAccountRemotelyInvalidated;

    const shouldDismissPickerForCreateCode =
      crossSigningStatus === CrossSigningStatusEnum.VERIFIED ||
      needsSecurityCodePromptAfterBootstrap === true ||
      isLoginUnlockResetCompleted();

    const showResetSection =
      !isLoginUnlock &&
      !suppressAutoReset &&
      ((!isLoadingVerifiedDevices && !isPinLoading && hasNoVerificationOptions) || isResettingKeys);

    // "Verify on another device" via SAS requires the sync loop to be running and the
    // cross-signing public identity to be available locally. That is the case:
    //   • post-login (isLoginUnlock=false): normal new-device-detected flow.
    //   • post_bootstrap gate: startClient has already run and the initial sync has
    //     populated the local crypto store, so requestOwnUserVerification works.
    //
    // At the pre_sync gate (before startClient) no sync is running. The button is still
    // shown, but clicking it resolves the gate immediately (bypasses to startClient) so
    // the flow falls through to the post_bootstrap gate where SAS actually works.
    const isPostBootstrap = isLoginUnlock && loginUnlockPhase === 'post_bootstrap';
    const showVerifyOnAnotherDevice =
      (!isLoginUnlock || isPostBootstrap) && devicesVerificationStatus === CrossSigningStatusEnum.VERIFIED;

    // For pre_sync: always show the button so the user can choose "verify on another device".
    // Clicking it resolves the pre_sync gate so startClient can proceed; bootstrapCrossSigning
    // will then detect needsSecretStorageUnlock and re-open the post_bootstrap gate where
    // the SAS flow actually runs (sync is active at that point).
    const isPreSync = isLoginUnlock && loginUnlockPhase === 'pre_sync';
    const showVerifyOnAnotherDevicePreSyncBypass = isPreSync;

    // Once a broken cross-signing identity is detected this session, the security code can never
    // succeed (poisoned account: `importCrossSigningKeys` always fails) — hide it and steer the user
    // to Reset keys instead of looping on a path that cannot work (BUG-013 Fix B).
    const showVerifyWithSecurityCode = !isPinLoading && hasSecurityCode === true && !isLoginUnlockBrokenIdentity();

    const hasOpenedResetRef = useRef(false);

    useEffect(() => {
      if (!showResetSection) {
        hasOpenedResetRef.current = false;
        return;
      }
      if (hasOpenedResetRef.current) return;
      hasOpenedResetRef.current = true;
      modal.remove();
      resetKeysModal.show();
    }, [showResetSection, modal, resetKeysModal]);

    // If the picker opened before the post-reset session flags propagated, dismiss it so
    // BootstrapSecurityCodeGate can show create-code instead (BUG-019 Fix B). Never during login
    // unlock — dismissing without completeLoginSecretStorageUnlock() deadlocks the gate (BUG-020).
    useEffect(() => {
      if (isLoginUnlock || !shouldDismissPickerForCreateCode) return;
      modal.remove();
    }, [isLoginUnlock, shouldDismissPickerForCreateCode, modal]);

    const handlePinPassphrase = () => {
      cancelVerification(false);
      setHasChosenSasVerification(false);
      modal.remove();
      unlockWithSecurityCodeModal.show({
        isLoginUnlock,
        onLoginUnlockCancel,
        onSubmitSecurityCode: isLoginUnlock ? submitLoginSecretStorageUnlock : undefined,
        onSuccess: () => {
          if (onVerificationComplete) onVerificationComplete();
        },
      });
    };

    const handleVerifyFromDevice = async () => {
      cancelVerification(false);
      setHasChosenSasVerification(true);
      modal.remove();
      await beginWaitingDeviceFlow();
    };

    // Pre-sync bypass: resolve the pre_sync gate immediately so startClient can run.
    // bootstrapCrossSigning will detect needsSecretStorageUnlock and re-open the gate in the
    // post_bootstrap phase, where requestOwnUserVerification works (sync active). We mark the durable
    // login-SAS signal — NOT hasChosenSasVerification — so the SAS stack stays login-aware across the
    // gap without prematurely trying to open the waiting modal (which would fail: no sync yet, and
    // the gate is closed → infinite spinner, BUG-006). The post_bootstrap picker auto-resumes below.
    const handleVerifyFromDevicePreSyncBypass = () => {
      cancelVerification(false);
      markLoginSasVerificationPending();
      modal.remove();
      completeLoginSecretStorageUnlock();
    };

    const handleOpenResetKeysModal = () => {
      modal.remove();
      resetKeysModal.show({
        isLoginUnlock,
        // "Not now"/close in the reset modal returns to this picker during login unlock.
        onBack: isLoginUnlock
          ? () => modal.show({ onVerificationComplete, isLoginUnlock: isLoginUnlockProp, onLoginUnlockCancel })
          : undefined,
      });
    };

    const handleLogout = () => {
      cancelVerification(false);
      setHasChosenSasVerification(false);
      modal.remove();

      if (isLoginUnlock) {
        if (onLoginUnlockCancel) onLoginUnlockCancel();
        else invokeLoginSecretStorageUnlockCancel();
        return;
      }

      const client = matrixClient ?? matrixClientRef.current;
      if (client) void logout(client);
    };

    if (showResetSection) return null;

    return (
      <Popup isVisible>
        <Popup.FullScreenContainer className="relative md:w-[512px]">
          <button
            type="button"
            data-testid={testingConstants.logoutOption}
            onClick={handleLogout}
            className="absolute right-[15px] top-[14.5px] z-10 flex h-8 cursor-pointer items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium leading-5 text-gray-70"
          >
            {t('logout')}
            <Logout className="h-5 w-5 shrink-0 stroke-gray-70 stroke-[1.5]" aria-hidden />
          </button>
          <div className="flex flex-col grow overflow-y-auto gap-6 px-6 pb-6 pt-12">
            <div className="flex flex-col items-center gap-8 px-2">
              <GradientIconWithShadow
                icon={DeviceUnverified}
                bgVariant="lightRed"
                iconVariant="alert"
                iconClassName="stroke-1.5"
                className="backdrop-blur-[20px]"
              />
              <div className="flex flex-col items-center gap-2 text-center">
                <h2 className="text-xl font-semibold leading-tight text-black">
                  {t('verification.new_device_detected')}
                </h2>
                <p className="text-base text-black">{t('verification.new_device_detected_subtitle')}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {showVerifyOnAnotherDevice && (
                <SelectionButtonWithIcon
                  title={t('verification.verify_on_another_device')}
                  icon={Devices}
                  iconVariant="primary"
                  onClick={handleVerifyFromDevice}
                />
              )}
              {showVerifyOnAnotherDevicePreSyncBypass && (
                <SelectionButtonWithIcon
                  title={t('verification.verify_on_another_device')}
                  icon={Devices}
                  iconVariant="primary"
                  onClick={handleVerifyFromDevicePreSyncBypass}
                />
              )}
              {showVerifyWithSecurityCode && (
                <SelectionButtonWithIcon
                  title={t('verification.verify_with_security_code')}
                  icon={LockPin}
                  iconVariant="primary"
                  onClick={handlePinPassphrase}
                />
              )}
              <SelectionButtonWithIcon
                title={t('verification.cant_verify_device')}
                icon={DeviceUnverified}
                iconVariant="grayBlack"
                onClick={handleOpenResetKeysModal}
              />
            </div>
          </div>
        </Popup.FullScreenContainer>
      </Popup>
    );
  }
);

export default ChooseVerificationMethodModal;
